import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle
} from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';
import environment from '../config/environment';

function codigoPuntoDisplay(idPunto) {
  if (idPunto == null || idPunto === '') return '';
  return `${environment.puntoChar || ''}${idPunto}`;
}

/** Etiqueta de puesto con prefijo REACT_APP_CHAR + id_punto (ej. M175-PINTO S.A.). */
function etiquetaPuntoConCodigo(p) {
  const codigo = codigoPuntoDisplay(p?.id_punto);
  const prefijo = codigo ? `${codigo}-` : '';
  return `${prefijo}${p?.etiqueta || ''}`;
}

/** Texto del select: codigo-nombre puesto — nombre ruta. */
function etiquetaPuntoEnRuta(p) {
  return `${etiquetaPuntoConCodigo(p)} — ${p?.ruta_nombre || 'Ruta'}`;
}

function distanciaMetros(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function etiquetaTurno(franja) {
  if (franja === 'N') return 'noche';
  if (franja === 'DN') return 'dia y noche';
  return 'dia';
}

function textoTurnoDelPunto(franja) {
  if (franja === 'DN') return 'dia y noche (un reporte por turno)';
  if (franja === 'N') return 'noche';
  return 'dia';
}

function mensajePuntoNoDisponibleTurno(p, turnoServidor) {
  const ahoraTxt = turnoServidor === 'N' ? 'turno noche' : 'turno dia';
  const m = p.marcas_franja_hoy || {};

  if (p.franja === 'D') {
    if (turnoServidor === 'N') {
      return `Este puesto esta configurado solo para turno dia en el cronograma. Ahora es ${ahoraTxt}.`;
    }
    return 'Este puesto esta configurado solo para turno dia y ya tiene reporte hoy en ese turno.';
  }

  if (p.franja === 'N') {
    if (turnoServidor === 'D') {
      return `Este puesto esta configurado solo para turno noche en el cronograma. Ahora es ${ahoraTxt}.`;
    }
    return 'Este puesto esta configurado solo para turno noche y ya tiene reporte hoy en ese turno.';
  }

  if (p.franja === 'DN') {
    if (turnoServidor === 'D' && m.D) {
      return 'Este puesto requiere un reporte en turno dia y otro en turno noche; ya envio el de dia.';
    }
    if (turnoServidor === 'N' && m.N) {
      return 'Este puesto requiere un reporte en turno dia y otro en turno noche; ya envio el de noche.';
    }
  }

  return `La configuracion de turno de este puesto no permite reportar ahora (${ahoraTxt}).`;
}

/** Turno servidor normalizado (misma base que franja_actual del API). */
function franjaTurnoActual(franjaActual) {
  return franjaActual === 'N' ? 'N' : 'D';
}

/** Marcas esperadas solo en el turno en curso (no suma el otro turno del dia). */
function marcasEsperadasEnTurno(franjaPunto, franjaActual) {
  const fa = franjaTurnoActual(franjaActual);
  if (franjaPunto === 'DN') return 1;
  if (franjaPunto === 'N') return fa === 'N' ? 1 : 0;
  return fa === 'D' ? 1 : 0;
}

function marcasReportadasEnTurno(p, franjaActual) {
  const m = p.marcas_franja_hoy || {};
  const fa = franjaTurnoActual(franjaActual);
  if (p.franja === 'DN') return fa === 'D' ? (m.D ? 1 : 0) : m.N ? 1 : 0;
  if (p.franja === 'N') return fa === 'N' && m.N ? 1 : 0;
  return fa === 'D' && m.D ? 1 : 0;
}

function aplicaEnTurnoActual(p, franjaActual) {
  return marcasEsperadasEnTurno(p.franja, franjaActual) > 0;
}

function pendienteEnTurnoActual(p, franjaActual) {
  return (
    aplicaEnTurnoActual(p, franjaActual) && marcasReportadasEnTurno(p, franjaActual) === 0
  );
}

function completadoEnTurnoActual(p, franjaActual) {
  return (
    aplicaEnTurnoActual(p, franjaActual) && marcasReportadasEnTurno(p, franjaActual) >= 1
  );
}

function DonutRuta({ total, reportados, etiquetaCentro, titulo, notaPie }) {
  const pendientes = Math.max(0, total - reportados);
  const r = 46;
  const circ = 2 * Math.PI * r;
  const pctR = total > 0 ? reportados / total : 0;
  const pctP = total > 0 ? pendientes / total : 0;
  const dashVerde = circ * pctR;
  const dashNaranja = circ * pctP;

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm h-full">
      <div className="flex-shrink-0 relative mx-auto" style={{ width: 132, height: 132 }}>
        <svg viewBox="0 0 120 120" className="w-full h-full" aria-hidden>
          <g transform="translate(60,60) rotate(-90)">
            <circle cx="0" cy="0" r={r} fill="none" stroke="#e5e7eb" strokeWidth="12" />
            {total > 0 && dashVerde > 0 && (
              <circle
                cx="0"
                cy="0"
                r={r}
                fill="none"
                stroke="#16a34a"
                strokeWidth="12"
                strokeLinecap="round"
                strokeDasharray={`${dashVerde} ${circ}`}
              />
            )}
            {total > 0 && dashNaranja > 0 && (
              <circle
                cx="0"
                cy="0"
                r={r}
                fill="none"
                stroke="#ea580c"
                strokeWidth="12"
                strokeLinecap="round"
                strokeDasharray={`${dashNaranja} ${circ}`}
                strokeDashoffset={-dashVerde}
              />
            )}
          </g>
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none text-center px-1">
          <span className="text-2xl font-bold text-slate-800 leading-none">{total}</span>
          <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500 mt-0.5">
            {etiquetaCentro || 'TOTAL'}
          </span>
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-800">{titulo}</p>
        <ul className="mt-3 space-y-2 text-sm text-slate-700">
          <li className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-full bg-green-600 shrink-0" />
            <span>Reportados: {reportados}</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-full bg-orange-600 shrink-0" />
            <span>Pendientes: {pendientes}</span>
          </li>
        </ul>
        {notaPie && <p className="mt-2 text-xs text-slate-500 leading-snug">{notaPie}</p>}
      </div>
    </div>
  );
}

function unirPuntosDelDia(estadoBundle) {
  const rutas = estadoBundle?.rutas_del_dia || [];
  const merged = [];
  for (const ruta of rutas) {
    for (const p of ruta.puntos || []) {
      merged.push({
        ...p,
        ruta_plan_id: ruta.ruta_plan?.id,
        ruta_nombre: ruta.ruta_plan?.nombre || 'Ruta',
        ruta_codigo: ruta.ruta_plan?.codigo_ruta || '—'
      });
    }
  }
  return merged;
}

const RutaProgramadaPanel = forwardRef(function RutaProgramadaPanel({ variant = 'page' }, ref) {
  const embedded = variant === 'embedded';
  const location = useLocation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadingSemanal, setLoadingSemanal] = useState(true);
  const [submittingId, setSubmittingId] = useState(null);
  const [estadoBundle, setEstadoBundle] = useState(null);
  const [resumenSemanal, setResumenSemanal] = useState(null);
  const [avisoInfo, setAvisoInfo] = useState('');
  const [modalExito, setModalExito] = useState(false);
  const [modalSeguimiento, setModalSeguimiento] = useState(false);
  const [modalConfirmarFuera, setModalConfirmarFuera] = useState(null);
  const [error, setError] = useState('');
  const [gpsMsg, setGpsMsg] = useState('');
  const [puntoSeleccionadoKey, setPuntoSeleccionadoKey] = useState(null);

  const [maxAccuracyMeters, setMaxAccuracyMeters] = useState(30);
  const [maxDistanceMeters, setMaxDistanceMeters] = useState(100);
  const [maxDistanceVirtualMeters, setMaxDistanceVirtualMeters] = useState(200);
  const [simulatedGPS, setSimulatedGPS] = useState(null);
  const [gpsCoords, setGpsCoords] = useState(null);
  const [gpsActivo, setGpsActivo] = useState(false);
  const watchIdRef = useRef(null);
  const exitoTimerRef = useRef(null);

  const mostrarExitoReporte = useCallback(() => {
    if (exitoTimerRef.current) clearTimeout(exitoTimerRef.current);
    setModalExito(true);
    exitoTimerRef.current = setTimeout(() => {
      setModalExito(false);
      exitoTimerRef.current = null;
    }, 2800);
  }, []);

  const cerrarModalExito = useCallback(() => {
    if (exitoTimerRef.current) {
      clearTimeout(exitoTimerRef.current);
      exitoTimerRef.current = null;
    }
    setModalExito(false);
  }, []);

  useEffect(() => {
    return () => {
      if (exitoTimerRef.current) clearTimeout(exitoTimerRef.current);
    };
  }, []);

  const cargarResumenSemanal = useCallback(async () => {
    if (user?.rol !== 'S') return;
    setLoadingSemanal(true);
    try {
      const url = new URL(getApiUrl('SUPERVISION', 'RUTA_PROGRAMADA_RESUMEN_SEMANAL'));
      url.searchParams.set('username', user.username || '');
      const res = await fetch(url.toString(), { headers: { 'Content-Type': 'application/json' } });
      const data = await res.json();
      if (res.ok && data.success && data.data) setResumenSemanal(data.data);
      else setResumenSemanal(null);
    } catch {
      setResumenSemanal(null);
    } finally {
      setLoadingSemanal(false);
    }
  }, [user]);

  const cargarEstado = useCallback(async () => {
    if (user?.rol !== 'S') return;
    setLoading(true);
    setError('');
    setAvisoInfo('');
    try {
      const url = new URL(getApiUrl('SUPERVISION', 'RUTA_PROGRAMADA_ESTADO'));
      url.searchParams.set('username', user.username || '');
      const ctrl = new AbortController();
      const to = setTimeout(() => ctrl.abort(), 25000);
      let res;
      try {
        res = await fetch(url.toString(), {
          headers: { 'Content-Type': 'application/json' },
          signal: ctrl.signal
        });
      } finally {
        clearTimeout(to);
      }
      const data = await res.json();
      if (!res.ok || !data.success) {
        setEstadoBundle(null);
        setError(data.error || 'No se pudo cargar la ruta');
        return;
      }
      if (!data.data) {
        setEstadoBundle(null);
        setAvisoInfo(data.message || 'Sin ruta para hoy');
        return;
      }
      setEstadoBundle(data.data);
      if (!(data.data.rutas_del_dia?.length)) {
        setAvisoInfo(data.message || 'Sin ruta para hoy');
      }
    } catch (e) {
      setEstadoBundle(null);
      setError(
        e.name === 'AbortError'
          ? 'La carga tardó demasiado. Intente de nuevo.'
          : e.message || 'Error de conexion'
      );
    } finally {
      setLoading(false);
    }
  }, [user]);

  const refrescarTodo = useCallback(async () => {
    await Promise.all([cargarEstado(), cargarResumenSemanal()]);
  }, [cargarEstado, cargarResumenSemanal]);

  useImperativeHandle(ref, () => ({
    refrescar: () => refrescarTodo()
  }), [refrescarTodo]);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await fetch(getApiUrl('SUPERVISION', 'CONFIG'), {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
        const data = await res.json();
        if (res.ok && data.success && data.data) {
          if (data.data.gpsMaxAccuracy != null) setMaxAccuracyMeters(Number(data.data.gpsMaxAccuracy));
          if (data.data.gpsMaxDistanceMeters != null) {
            setMaxDistanceMeters(Number(data.data.gpsMaxDistanceMeters));
          }
          if (data.data.gpsMaxDistanceVirtual != null) {
            setMaxDistanceVirtualMeters(Number(data.data.gpsMaxDistanceVirtual));
          }
          if (data.data.simulatedGPS?.enabled) setSimulatedGPS(data.data.simulatedGPS);
        }
      } catch {
        /* defaults */
      }
    };
    fetchConfig();
  }, []);

  useEffect(() => {
    refrescarTodo();
  }, [refrescarTodo]);

  useEffect(() => {
    if (!location.state?.rutaReportadaExito) return;
    setPuntoSeleccionadoKey(null);
    refrescarTodo();
    mostrarExitoReporte();
  }, [location.state?.rutaReportadaExito, refrescarTodo, mostrarExitoReporte]);

  const puntosUnificados = useMemo(() => unirPuntosDelDia(estadoBundle), [estadoBundle]);
  const franjaActualServidor = estadoBundle?.franja_actual ?? null;

  useEffect(() => {
    if (user?.rol !== 'S' || !puntosUnificados.length) {
      if (watchIdRef.current != null && watchIdRef.current !== 999999 && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      setGpsActivo(false);
      return undefined;
    }

    if (simulatedGPS?.enabled) {
      const tick = () => {
        setGpsCoords({
          lat: simulatedGPS.lat,
          lng: simulatedGPS.lng,
          acu: simulatedGPS.accuracy ?? 10
        });
        setGpsActivo(true);
      };
      tick();
      watchIdRef.current = 999999;
      const id = setInterval(tick, 2000);
      return () => {
        clearInterval(id);
        watchIdRef.current = null;
        setGpsActivo(false);
      };
    }

    if (!navigator.geolocation) {
      setGpsActivo(false);
      return undefined;
    }

    const wid = navigator.geolocation.watchPosition(
      (pos) => {
        const acu = pos.coords.accuracy ?? null;
        setGpsCoords({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          acu
        });
        setGpsActivo(true);
      },
      () => {
        setGpsActivo(false);
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );
    watchIdRef.current = wid;

    return () => {
      if (wid != null && navigator.geolocation) navigator.geolocation.clearWatch(wid);
      watchIdRef.current = null;
      setGpsActivo(false);
    };
  }, [user?.rol, puntosUnificados.length, simulatedGPS]);

  const puntosConCercania = useMemo(() => {
    return puntosUnificados.map((p) => {
      const latP = p.latitud != null ? parseFloat(p.latitud) : NaN;
      const lngP = p.longitud != null ? parseFloat(p.longitud) : NaN;
      const tieneCoords = Number.isFinite(latP) && Number.isFinite(lngP);
      const esVirtual =
        String(p.origen_punto || '').toUpperCase() === 'VIRTUAL' ||
        String(p.id_generado || '').startsWith('GV:') ||
        String(p.id_generado || '').startsWith('SF:');
      const radioPunto = esVirtual ? maxDistanceVirtualMeters : maxDistanceMeters;

      let distanciaM = null;
      let dentroDelRadio = false;
      if (
        tieneCoords &&
        gpsCoords &&
        gpsCoords.lat != null &&
        gpsCoords.lng != null &&
        gpsCoords.acu != null &&
        gpsCoords.acu <= maxAccuracyMeters
      ) {
        distanciaM = distanciaMetros(gpsCoords.lat, gpsCoords.lng, latP, lngP);
        dentroDelRadio = distanciaM <= radioPunto;
      }

      return { ...p, distanciaM, dentroDelRadio, tieneCoords, esVirtual, radioPunto };
    });
  }, [
    puntosUnificados,
    gpsCoords,
    maxAccuracyMeters,
    maxDistanceMeters,
    maxDistanceVirtualMeters
  ]);

  const puntosPendientesLista = useMemo(
    () =>
      puntosConCercania.filter((p) =>
        pendienteEnTurnoActual(p, franjaActualServidor)
      ),
    [puntosConCercania, franjaActualServidor]
  );

  const puntosParaReportar = useMemo(
    () =>
      puntosPendientesLista
        .filter(
          (p) =>
            p.disponible_ahora &&
            p.tieneCoords &&
            !completadoEnTurnoActual(p, franjaActualServidor)
        )
        .sort((a, b) => (a.distanciaM ?? 999999) - (b.distanciaM ?? 999999)),
    [puntosPendientesLista, franjaActualServidor]
  );

  const puntoActivo = useMemo(() => {
    if (!puntoSeleccionadoKey) return null;
    return (
      puntosParaReportar.find(
        (p) => `${p.ruta_plan_id}:${p.id_generado}` === puntoSeleccionadoKey
      ) ?? null
    );
  }, [puntosParaReportar, puntoSeleccionadoKey]);

  const puntoKey = (p) => `${p.ruta_plan_id}:${p.id_generado}`;

  const resumenRutas = useMemo(() => {
    const rutas = estadoBundle?.rutas_del_dia || [];
    return rutas
      .map((r) => {
        let esperados = 0;
        let reportados = 0;
        for (const p of r.puntos || []) {
          esperados += marcasEsperadasEnTurno(p.franja, franjaActualServidor);
          reportados += marcasReportadasEnTurno(p, franjaActualServidor);
        }
        return {
          id: r.ruta_plan?.id,
          nombre: r.ruta_plan?.nombre || 'Ruta',
          codigo: r.ruta_plan?.codigo_ruta || '—',
          reportados,
          esperados
        };
      })
      .filter((r) => r.esperados > 0);
  }, [estadoBundle, franjaActualServidor]);

  const statsFranjaHoy = useMemo(() => {
    if (!puntosUnificados.length) {
      return { esperadosD: 0, esperadosN: 0, reportadosD: 0, reportadosN: 0 };
    }
    let esperadosD = 0;
    let esperadosN = 0;
    let reportadosD = 0;
    let reportadosN = 0;
    for (const p of puntosUnificados) {
      const m = p.marcas_franja_hoy || {};
      if (p.franja === 'DN') {
        esperadosD += 1;
        esperadosN += 1;
        if (m.D) reportadosD += 1;
        if (m.N) reportadosN += 1;
      } else if (p.franja === 'N') {
        esperadosN += 1;
        if (m.N) reportadosN += 1;
      } else {
        esperadosD += 1;
        if (m.D) reportadosD += 1;
      }
    }
    return { esperadosD, esperadosN, reportadosD, reportadosN };
  }, [puntosUnificados]);

  const statsTurnoActual = useMemo(() => {
    const fa = franjaTurnoActual(franjaActualServidor);
    if (fa === 'N') {
      return {
        esperados: statsFranjaHoy.esperadosN,
        reportados: statsFranjaHoy.reportadosN,
        etiquetaTurno: 'noche'
      };
    }
    return {
      esperados: statsFranjaHoy.esperadosD,
      reportados: statsFranjaHoy.reportadosD,
      etiquetaTurno: 'dia'
    };
  }, [franjaActualServidor, statsFranjaHoy]);

  const statsSemanaTurnoActual = useMemo(() => {
    if (!resumenSemanal) {
      return { esperados: 0, reportados: 0, etiquetaTurno: 'dia' };
    }
    const fa = franjaTurnoActual(franjaActualServidor);
    const espD = Number(resumenSemanal.esperados_franja_dia ?? 0);
    const repD = Number(resumenSemanal.reportados_franja_dia ?? 0);
    const espN = Number(resumenSemanal.esperados_franja_noche ?? 0);
    const repN = Number(resumenSemanal.reportados_franja_noche ?? 0);
    if (fa === 'N') {
      return { esperados: espN, reportados: repN, etiquetaTurno: 'noche' };
    }
    return { esperados: espD, reportados: repD, etiquetaTurno: 'dia' };
  }, [resumenSemanal, franjaActualServidor]);

  const enviarReporte = async (punto, { confirmarUbicacionEnPunto = false } = {}) => {
    if (!user?.username || !punto?.ruta_plan_id || !punto?.id_generado) return;
    const idBtn = puntoKey(punto);
    setSubmittingId(idBtn);
    setError('');
    setGpsMsg('Obteniendo ubicacion…');
    try {
      let pos;
      if (simulatedGPS?.enabled) {
        pos = {
          lat: simulatedGPS.lat,
          lng: simulatedGPS.lng,
          acu: simulatedGPS.accuracy ?? 10
        };
      } else {
        pos = await new Promise((resolve, reject) => {
          if (!navigator.geolocation) {
            reject(new Error('Geolocalizacion no disponible'));
            return;
          }
          navigator.geolocation.getCurrentPosition(
            (p) => {
              resolve({
                lat: p.coords.latitude,
                lng: p.coords.longitude,
                acu: p.coords.accuracy || null
              });
            },
            (err) => reject(new Error(err.message || 'No se pudo obtener la ubicacion')),
            { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
          );
        });
      }

      if (!pos.acu || pos.acu > maxAccuracyMeters) {
        setGpsMsg('');
        setError(`Precision GPS insuficiente (max. ${maxAccuracyMeters} m).`);
        return;
      }

      setGpsMsg('Enviando reporte…');
      const res = await fetch(getApiUrl('SUPERVISION', 'RUTA_PROGRAMADA_TIMBRAR'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: user.username,
          appid: user.appid,
          ruta_plan_id: punto.ruta_plan_id,
          id_generado: punto.id_generado,
          lat: pos.lat,
          lng: pos.lng,
          acu: pos.acu,
          confirmar_ubicacion_en_punto: confirmarUbicacionEnPunto
        })
      });
      const data = await res.json();
      setGpsMsg('');
      if (!res.ok || !data.success) {
        setError(data.error || 'No se pudo enviar el reporte');
        return;
      }
      if (data.solo_seguimiento) {
        setError('');
        setModalSeguimiento(true);
        return;
      }
      setPuntoSeleccionadoKey(null);
      await refrescarTodo();
      mostrarExitoReporte();
    } catch (e) {
      setGpsMsg('');
      setError(e.message || 'Error');
    } finally {
      setSubmittingId(null);
    }
  };

  if (user?.rol !== 'S') return null;

  const outerClass = embedded ? 'space-y-4' : 'max-w-5xl mx-auto px-4 py-6 sm:py-8';
  const TitleTag = embedded ? 'h2' : 'h1';
  const titleClass = embedded ? 'text-lg font-semibold text-gray-900' : 'text-xl sm:text-2xl font-semibold text-gray-900';

  const hayRutaHoy = (estadoBundle?.rutas_del_dia?.length ?? 0) > 0;
  const faTurno = franjaTurnoActual(franjaActualServidor);
  const relojSimulado = Boolean(estadoBundle?.reloj_simulado);
  const labelFranjaSim =
    String(estadoBundle?.franja_actual || '').toUpperCase() === 'N' ? 'Noche' : 'Día';

  if (embedded) {
    if (loading) return null;
    if (!hayRutaHoy) return null;
  }

  const contenidoPanel = (
    <>
      <header className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <TitleTag className={titleClass}>Operacion de rutas programadas</TitleTag>
          
        </div>
        {!embedded && (
          <button
            type="button"
            onClick={() => refrescarTodo()}
            disabled={loading || loadingSemanal}
            className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Actualizar rutas
          </button>
        )}
      </header>

      {relojSimulado && (
        <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950">
          <p className="font-semibold">Reloj de rutas simulado (solo desarrollo)</p>
          <p className="mt-0.5 text-xs text-amber-900">
            Referencia: {estadoBundle?.fecha_hora_referencia || '—'}
            {' · '}
            Día operativo: {estadoBundle?.dia_operativo || '—'}
            {' · '}
            Turno: {labelFranjaSim}
            {' · '}
            Fecha operativa: {estadoBundle?.fecha_operativa || '—'}
          </p>
          <p className="mt-1 text-xs text-amber-800">
            Vacía <code className="font-mono">CALENDARIO_HORARIOS_FECHA_REFERENCIA</code> en el .env y reinicia el backend para usar la hora real.
          </p>
        </div>
      )}

      {(loading || loadingSemanal) && !estadoBundle && !resumenSemanal && (
        <p className="text-sm text-gray-600 mb-4">Cargando…</p>
      )}

      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>
      )}
      {avisoInfo && <p className="mb-4 text-sm text-gray-600">{avisoInfo}</p>}

      {resumenRutas.length > 0 && (
        <section className="mb-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
          <p className="text-xs font-semibold text-gray-700 mb-1">Tus rutas de hoy</p>
          <p className="text-xs text-gray-500 mb-2">
            {puntosUnificados.length} punto(s) en ruta · {puntosParaReportar.length} pendiente(s) en tu turno
          </p>
          <div className="flex flex-wrap gap-2">
            {resumenRutas.map((r) => (
              <span
                key={r.id}
                className="inline-flex items-center rounded-full border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-700"
              >
                {r.nombre} ({r.codigo}) · {r.reportados}/{r.esperados}
              </span>
            ))}
          </div>
        </section>
      )}

      <section className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
        <p className="text-sm font-semibold text-gray-900">Reportar punto de la ruta</p>
        <p className="mt-0.5 text-xs text-gray-600">
          Seleccione un puesto pendiente de su turno (radio max.{' '}
          {puntoActivo
            ? `${puntoActivo.radioPunto ?? (puntoActivo.esVirtual ? maxDistanceVirtualMeters : maxDistanceMeters)} m`
            : `${maxDistanceMeters} m · Virtual ${maxDistanceVirtualMeters} m`}
          ).
        </p>
        {puntosParaReportar.length === 0 ? (
          <p className="mt-2 text-xs text-gray-600">
            No hay puntos pendientes con turno habilitado en este momento.
          </p>
        ) : (
          <>
            <div className="mt-3">
              <label htmlFor="select-punto-ruta" className="sr-only">
                Punto de la ruta
              </label>
              <select
                id="select-punto-ruta"
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900"
                value={puntoSeleccionadoKey || ''}
                onChange={(e) => setPuntoSeleccionadoKey(e.target.value || null)}
              >
                <option value="">Seleccione un punto…</option>
                {puntosParaReportar.map((p) => {
                  const key = puntoKey(p);
                  return (
                    <option key={key} value={key}>
                      {etiquetaPuntoEnRuta(p)}
                    </option>
                  );
                })}
              </select>
            </div>

            {puntoActivo && (
              <div className="mt-3 rounded-md border border-gray-200 bg-white p-3 space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-900">{etiquetaPuntoConCodigo(puntoActivo)}</p>
                  <p className="text-xs text-gray-600">
                    {puntoActivo.ruta_nombre} · {textoTurnoDelPunto(puntoActivo.franja)}
                  </p>
                </div>

                {!gpsActivo || !gpsCoords ? (
                  <p className="text-xs text-amber-800">Obteniendo ubicacion GPS…</p>
                ) : gpsCoords.acu > maxAccuracyMeters ? (
                  <p className="text-xs text-red-700">
                    Precision GPS insuficiente (max. {maxAccuracyMeters} m). Intente de nuevo en un momento.
                  </p>
                ) : puntoActivo.dentroDelRadio ? (
                  <>
                    <p className="text-xs text-green-700">
                      Dentro del radio ({puntoActivo.distanciaM != null ? puntoActivo.distanciaM.toFixed(0) : '?'} m).
                    </p>
                    <button
                      type="button"
                      onClick={() => enviarReporte(puntoActivo)}
                      disabled={submittingId === puntoSeleccionadoKey}
                      className="w-full inline-flex justify-center px-4 py-2 text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300"
                    >
                      {submittingId === puntoSeleccionadoKey ? 'Enviando…' : 'Reportar'}
                    </button>
                  </>
                ) : (
                  <>
                    <div
                      className="rounded-md border border-red-300 bg-red-50 px-3 py-2"
                      role="alert"
                    >
                      <p className="text-sm font-semibold text-red-800">La ubicacion no coincide</p>
                      <p className="text-xs text-red-700 mt-1">
                        Esta a{' '}
                        {puntoActivo.distanciaM != null
                          ? puntoActivo.distanciaM.toFixed(0)
                          : '?'}{' '}
                        m del punto (max. {puntoActivo.radioPunto ?? maxDistanceMeters} m).
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setModalConfirmarFuera(puntoActivo)}
                      disabled={submittingId === puntoSeleccionadoKey}
                      className="w-full inline-flex justify-center px-4 py-2 text-sm font-medium rounded-md border border-red-300 bg-white text-red-800 hover:bg-red-50 disabled:opacity-50"
                    >
                      {submittingId === puntoSeleccionadoKey
                        ? 'Enviando…'
                        : 'Estoy en el punto pero no se detecta'}
                    </button>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </section>

      <div className="mb-4 -mx-1 overflow-x-auto overscroll-x-contain pb-1">
        <div className="flex gap-3 px-1 snap-x snap-mandatory">
          <div className="snap-start shrink-0 w-[min(calc(100vw-2.5rem),340px)]">
            <DonutRuta
              total={statsTurnoActual.esperados}
              reportados={statsTurnoActual.reportados}
              etiquetaCentro={faTurno === 'N' ? 'NOCHE' : 'DIA'}
              titulo={`Tu turno (${statsTurnoActual.etiquetaTurno})`}
              notaPie={`Dia completo en ruta: ${statsFranjaHoy.reportadosD}/${statsFranjaHoy.esperadosD} · Noche: ${statsFranjaHoy.reportadosN}/${statsFranjaHoy.esperadosN}.`}
            />
          </div>
          <div className="snap-start shrink-0 w-[min(calc(100vw-2.5rem),340px)]">
            {resumenSemanal ? (
              <DonutRuta
                total={statsSemanaTurnoActual.esperados}
                reportados={statsSemanaTurnoActual.reportados}
                etiquetaCentro={faTurno === 'N' ? 'NOCHE' : 'DIA'}
                titulo={`Tu turno esta semana (${statsSemanaTurnoActual.etiquetaTurno})`}
                notaPie={`Semana completa (todos los turnos): ${resumenSemanal.reportados_semana ?? 0}/${resumenSemanal.esperados_semana ?? 0} · Dia: ${resumenSemanal.reportados_franja_dia ?? 0}/${resumenSemanal.esperados_franja_dia ?? 0} · Noche: ${resumenSemanal.reportados_franja_noche ?? 0}/${resumenSemanal.esperados_franja_noche ?? 0} · ${resumenSemanal.rutas_cronograma_activas} ruta(s) activas.`}
              />
            ) : (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600 flex items-center justify-center min-h-[220px] h-full">
                {loadingSemanal ? 'Cargando resumen semanal…' : 'No se pudo cargar el resumen semanal.'}
              </div>
            )}
          </div>
        </div>
      </div>

      {gpsMsg && <p className="mt-3 text-xs text-gray-600">{gpsMsg}</p>}

    </>
  );

  const modalExitoEl = modalExito ? (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-exito-ruta-title"
      onClick={cerrarModalExito}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6 text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
          <svg className="h-7 w-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 id="modal-exito-ruta-title" className="text-lg font-semibold text-gray-900">
          Reporte enviado
        </h2>
        <p className="mt-2 text-sm text-gray-600">Reporte enviado correctamente.</p>
        <button
          type="button"
          onClick={cerrarModalExito}
          className="mt-5 w-full inline-flex justify-center px-4 py-2 text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
        >
          Aceptar
        </button>
      </div>
    </div>
  ) : null;

  const modalSeguimientoEl = modalSeguimiento ? (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-seguimiento-ruta-title"
      onClick={() => setModalSeguimiento(false)}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6 text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
          <svg className="h-7 w-7 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 id="modal-seguimiento-ruta-title" className="text-lg font-semibold text-gray-900">
          No detectado
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          Se registro para seguimiento. El punto sigue pendiente: acerquese al sitio y use Reportar cuando el GPS
          confirme que esta dentro del radio para poder reportar.
        </p>
        <button
          type="button"
          onClick={() => setModalSeguimiento(false)}
          className="mt-5 w-full inline-flex justify-center px-4 py-2 text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
        >
          Aceptar
        </button>
      </div>
    </div>
  ) : null;

  const modalConfirmarFueraEl = modalConfirmarFuera ? (
    <div
      className="fixed inset-0 z-[75] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-confirmar-fuera-title"
      onClick={() => setModalConfirmarFuera(null)}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="modal-confirmar-fuera-title" className="text-lg font-semibold text-gray-900">
          ¿Esta seguro?
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          Registrar seguimiento en ruta para{' '}
          <span className="font-medium text-gray-800">{etiquetaPuntoConCodigo(modalConfirmarFuera)}</span>. No cuenta
          como reporte de ruta: debe estar dentro del radio para poder reportar.
        </p>
        <div className="mt-5 flex gap-3">
          <button
            type="button"
            onClick={() => setModalConfirmarFuera(null)}
            className="flex-1 inline-flex justify-center px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
          >
            No
          </button>
          <button
            type="button"
            onClick={() => {
              const p = modalConfirmarFuera;
              setModalConfirmarFuera(null);
              enviarReporte(p, { confirmarUbicacionEnPunto: true });
            }}
            disabled={submittingId === puntoKey(modalConfirmarFuera)}
            className="flex-1 inline-flex justify-center px-4 py-2 text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300"
          >
            {submittingId === puntoKey(modalConfirmarFuera) ? 'Enviando…' : 'Sí'}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  if (embedded) {
    return (
      <>
        <section id="ruta-programada" className="space-y-3" aria-label="Ruta programada">
          <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 shadow-sm space-y-4">
            {contenidoPanel}
          </div>
        </section>
        {modalConfirmarFueraEl}
        {modalSeguimientoEl}
        {modalExitoEl}
      </>
    );
  }

  return (
    <>
      <div className={outerClass}>{contenidoPanel}</div>
      {modalConfirmarFueraEl}
      {modalSeguimientoEl}
      {modalExitoEl}
    </>
  );
});

export default RutaProgramadaPanel;
